import { useCallback, useMemo, useReducer } from "react";

import { IInputSlot } from "@/api/agents/agents.types";
import { IAttachmentOption } from "@/api/attachments/attachments.types";

type SourceKind = "library" | "upload";

interface ActivePicker {
    slotIndex: number;
    source: SourceKind;
}

interface InputGateState {
    /** Collected documents per slot, indexed parallel to the scenario's slots. */
    slotDocs: IAttachmentOption[][];
    /** Slot + source whose picker modal is open, if any. */
    activePicker: ActivePicker | null;
}

type Action =
    | { type: "openPicker"; slotIndex: number; source: SourceKind }
    | { type: "closePicker" }
    | {
          type: "addDocs";
          slotIndex: number;
          docs: IAttachmentOption[];
          slot: IInputSlot;
      }
    | { type: "removeDoc"; slotIndex: number; docIndex: number }
    | { type: "reset" };

const init = (slotCount: number): InputGateState => ({
    slotDocs: Array.from({ length: slotCount }, () => []),
    activePicker: null,
});

/**
 * The max_count clamp, in exactly one place so the Library and Upload paths
 * cannot drift: 1 replaces, null appends every pick, a cap appends only as many
 * as the remaining headroom allows.
 */
const clampInto = (
    existing: IAttachmentOption[],
    incoming: IAttachmentOption[],
    maxCount: number | null
): IAttachmentOption[] => {
    if (maxCount === 1) return incoming.slice(0, 1);
    if (maxCount === null) return [...existing, ...incoming];
    const headroom = Math.max(0, maxCount - existing.length);
    return [...existing, ...incoming.slice(0, headroom)];
};

const reducer = (state: InputGateState, action: Action): InputGateState => {
    switch (action.type) {
        case "openPicker":
            return {
                ...state,
                activePicker: {
                    slotIndex: action.slotIndex,
                    source: action.source,
                },
            };
        case "closePicker":
            return { ...state, activePicker: null };
        case "addDocs": {
            const slotDocs = state.slotDocs.map((docs, i) =>
                i === action.slotIndex
                    ? clampInto(docs, action.docs, action.slot.max_count)
                    : docs
            );
            return { ...state, slotDocs, activePicker: null };
        }
        case "removeDoc": {
            const slotDocs = state.slotDocs.map((docs, i) =>
                i === action.slotIndex
                    ? docs.filter((_, d) => d !== action.docIndex)
                    : docs
            );
            return { ...state, slotDocs };
        }
        case "reset":
            return init(state.slotDocs.length);
        default:
            return state;
    }
};

const slotStateWord = (slot: IInputSlot, count: number): string => {
    if (count >= slot.min_count) return "satisfied";
    if (count === 0) return "no document yet";
    return `${count} of ${slot.min_count} documents`;
};

/** A slot projected for rendering: its docs plus all derived display state. */
export interface SlotView {
    slot: IInputSlot;
    index: number;
    docs: IAttachmentOption[];
    satisfied: boolean;
    /** e.g. "Current Policy, required, satisfied" — state as text, not color. */
    accessibleName: string;
    /** Whether another document still fits under max_count. */
    canAddMore: boolean;
    libraryOpen: boolean;
    uploadOpen: boolean;
}

export interface UseInputGate {
    slots: SlotView[];
    readyRequiredDocs: number;
    totalRequiredDocs: number;
    allRequiredSatisfied: boolean;
    /**
     * True while any slot's Library/Upload picker is open. Start is gated on
     * this so a picker confirm cannot resolve onto an unmounting instance.
     */
    anyPickerOpen: boolean;
    /**
     * All collected documents in authored slot order, then pick order within a
     * slot. Slot labels are not sent to the model, so this order is the only
     * disambiguation signal — never sort it.
     */
    allCollectedFlat: IAttachmentOption[];
    hasAnyDocs: boolean;
    openPicker: (slotIndex: number, source: SourceKind) => void;
    closePicker: () => void;
    addDocs: (slotIndex: number, docs: IAttachmentOption[]) => void;
    removeDoc: (slotIndex: number, docIndex: number) => void;
    reset: () => void;
}

/**
 * Owns every piece of Input Gate state (collected docs, the open picker) and
 * the derived, memoized progress. The modal that consumes it stays
 * presentational and only emits `allCollectedFlat` on start.
 */
export const useInputGate = (slots: IInputSlot[]): UseInputGate => {
    const [state, dispatch] = useReducer(reducer, slots.length, init);

    const openPicker = useCallback(
        (slotIndex: number, source: SourceKind) =>
            dispatch({ type: "openPicker", slotIndex, source }),
        []
    );
    const closePicker = useCallback(
        () => dispatch({ type: "closePicker" }),
        []
    );
    const addDocs = useCallback(
        (slotIndex: number, docs: IAttachmentOption[]) =>
            dispatch({
                type: "addDocs",
                slotIndex,
                docs,
                slot: slots[slotIndex],
            }),
        [slots]
    );
    const removeDoc = useCallback(
        (slotIndex: number, docIndex: number) =>
            dispatch({ type: "removeDoc", slotIndex, docIndex }),
        []
    );
    const reset = useCallback(() => dispatch({ type: "reset" }), []);

    return useMemo(() => {
        // Required-slot progress accumulates in the same pass that builds the
        // views, so the slot array is walked exactly once per state change.
        let totalRequiredDocs = 0;
        let readyRequiredDocs = 0;
        let allRequiredSatisfied = true;

        const slotViews: SlotView[] = slots.map((slot, index) => {
            const docs = state.slotDocs[index] ?? [];
            const satisfied = docs.length >= slot.min_count;
            if (slot.required) {
                totalRequiredDocs += slot.min_count;
                readyRequiredDocs += Math.min(docs.length, slot.min_count);
                if (!satisfied) allRequiredSatisfied = false;
            }
            return {
                slot,
                index,
                docs,
                satisfied,
                accessibleName: `${slot.label}, ${
                    slot.required ? "required" : "optional"
                }, ${slotStateWord(slot, docs.length)}`,
                canAddMore:
                    slot.max_count === null || docs.length < slot.max_count,
                libraryOpen:
                    state.activePicker?.slotIndex === index &&
                    state.activePicker.source === "library",
                uploadOpen:
                    state.activePicker?.slotIndex === index &&
                    state.activePicker.source === "upload",
            };
        });

        const allCollectedFlat = state.slotDocs.flat();

        return {
            slots: slotViews,
            readyRequiredDocs,
            totalRequiredDocs,
            allRequiredSatisfied,
            anyPickerOpen: state.activePicker !== null,
            allCollectedFlat,
            hasAnyDocs: allCollectedFlat.length > 0,
            openPicker,
            closePicker,
            addDocs,
            removeDoc,
            reset,
        };
    }, [slots, state, openPicker, closePicker, addDocs, removeDoc, reset]);
};
